import BottomSheet, {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetView,
} from '@gorhom/bottom-sheet';
import { Link, useLocalSearchParams, useRouter } from 'expo-router';
import { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Hide, mq, withUnistyles } from 'react-native-unistyles';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import type { ButtonVariant } from '@/components/UI/Button';
import Text from '@/components/UI/Text';
import type { FragmentOf } from '@/gql/graphql';
import type { EventScorekeepingPanelFormValues } from '../../EventScorekeeping.types';
import EventScorekeepingPanelPreviewRecipientList from './EventScorekeepingPanelPreviewRecipientList';
import EventScorekeepingPanelPreviewTime, {
  type EventScorekeepingPanelPreviewTimeFragment,
} from './EventScorekeepingPanelPreviewTime';

const UnistylesBottomSheet = withUnistyles(BottomSheet, (theme, rt) => ({
  topInset: rt.insets.top,
  backgroundStyle: {
    backgroundColor: theme.colors.surface,
  },
  handleStyle: {
    display: 'none' as const,
  },
}));

function EventScorekeepingPanelPreview({
  basePath,
  onSubmit,
  timeFragment,
}: {
  basePath: string;
  onSubmit: () => Promise<void>;
  timeFragment:
    | FragmentOf<typeof EventScorekeepingPanelPreviewTimeFragment>
    | null
    | undefined;
}) {
  const [sheetIndex, setSheetIndex] = useState(0);
  const bottomSheetRef = useRef<BottomSheet>(null);
  const { badgeAssigneeId, eventId } = useLocalSearchParams<{
    badgeAssigneeId?: string;
    eventId?: string;
  }>();
  const { push, setParams } = useRouter();
  const form = useFormContext<EventScorekeepingPanelFormValues>();
  const { control } = form;
  const firstRecipientId = useWatch({
    control,
    name: 'recipients.0.id',
  });
  const selectedRecipientId = useWatch({
    control,
    name: 'selectedRecipientId',
  });
  const selectedTime = useWatch({
    control,
    name: 'selectedTime',
  });
  const renderBackdrop = (props: BottomSheetBackdropProps) =>
    selectedRecipientId ? (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={1}
        disappearsOnIndex={0}
        pressBehavior="none"
      />
    ) : null;
  const onPressCancel = () => {
    form.reset(
      {
        ...form.formState.defaultValues,
        selectedTeam: form.getValues('selectedTeam'),
        isEditing: false,
      },
      {
        keepDefaultValues: true,
      },
    );
    setParams({
      badgeAssigneeId: undefined,
    });
  };
  const onPressDelete = () => {
    if (!badgeAssigneeId || !eventId) {
      return;
    }

    setParams({
      badgeAssigneeId: undefined,
    });
    push({
      pathname: '/event/[eventId]/scorekeeping/delete',
      params: {
        badgeAssigneeId,
        eventId,
      },
    });
  };
  const onChange = (index: number) => {
    setSheetIndex(index);
  };
  const isSheetOpen = sheetIndex === 1;
  const saveButtonInfo: {
    text: string;
    action?: ButtonVariant;
  } = badgeAssigneeId
    ? { text: 'Save' }
    : { text: 'Assign Stat', action: 'success' };
  return (
    <UnistylesBottomSheet
      ref={bottomSheetRef}
      backdropComponent={renderBackdrop}
      index={sheetIndex}
      enableContentPanningGesture={false}
      enableHandlePanningGesture={false}
      onChange={onChange}
      keyboardBlurBehavior="restore"
    >
      <BottomSheetView>
        <Box
          direction="horizontal"
          space="sm"
          justifyContent="space-between"
          alignItems="center"
          flex
        >
          {firstRecipientId || badgeAssigneeId ? (
            <Box direction="horizontal" p="md" space="md" alignItems="center">
              <ActionButton
                icon="close-outline"
                label={`Stop ${badgeAssigneeId ? 'Editing' : 'Assigning'}`}
                onPress={onPressCancel}
                variant="secondary"
              />

              {badgeAssigneeId ? (
                <ActionButton
                  icon="trash-outline"
                  label="Delete..."
                  onPress={onPressDelete}
                  variant="error"
                />
              ) : null}
            </Box>
          ) : null}
          {(firstRecipientId || badgeAssigneeId) && selectedTime ? (
            <EventScorekeepingPanelPreviewTime fragment={timeFragment} />
          ) : null}
        </Box>
        <Box>
          {firstRecipientId ? (
            <EventScorekeepingPanelPreviewRecipientList />
          ) : (
            <Box
              alignItems="center"
              direction="horizontal"
              p="xl"
              justifyContent="space-between"
            >
              {!isSheetOpen ? (
                <Text flexShrink={1} size="xl" variant="typographySecondary">
                  Select a Stat and a Member above.
                </Text>
              ) : null}
              <Hide mq={mq.only.width('lg')}>
                <Link
                  asChild
                  href={{
                    pathname: '/event/[eventId]/scorekeeping/assignment',
                    params: {
                      basePath,
                      badgeAssigneeId,
                      eventId,
                    },
                  }}
                >
                  <ActionButton
                    icon="create-outline"
                    forceLabel
                    variant="secondary"
                    label="Edit Stats"
                  />
                </Link>
              </Hide>
            </Box>
          )}
        </Box>
        {!isSheetOpen ? (
          <FormSaveButton info={saveButtonInfo} onPress={onSubmit} />
        ) : null}
      </BottomSheetView>
    </UnistylesBottomSheet>
  );
}
export default EventScorekeepingPanelPreview;
